import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { Screen, Text, Card, Badge, Button } from "@andrum/ui";
import type { WellbeingCategory } from "@andrum/design-tokens";
import { useExperimentStore } from "@/stores/experiment-store";
import {
  buildToolbox,
  deriveSimpleInsights,
} from "@/features/insights/utils/derive-insights";
import { buildMainInsight } from "@/features/insights/utils/main-insight";
import { listRecentCheckIns } from "@/features/check-in/api/check-in-api";
import type { CheckInRow } from "@andrum/database";
import {
  buildChartSeries,
  type ChartRange,
} from "@/features/insights/utils/week-stats";
import {
  MetricToggle,
  RangeToggle,
  WeekChart,
} from "@/features/insights/components/WeekChart";
import { CategoryHeat } from "@/features/insights/components/CategoryHeat";
import {
  fetchWeeklySummary,
  type WeeklySummaryResponse,
} from "@/features/insights/api/weekly-api";

function isCategory(value: string): value is WellbeingCategory {
  return (
    value === "connection" ||
    value === "movement" ||
    value === "recovery" ||
    value === "gratitude" ||
    value === "meaning" ||
    value === "presence"
  );
}

export function InsightsScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    outcomes,
    assignments,
    experiments,
    hydrate,
    hydrated,
    loading: experimentsLoading,
    assignFromCatalog,
  } = useExperimentStore();
  const [checkIns, setCheckIns] = useState<CheckInRow[]>([]);
  const [checkInsReady, setCheckInsReady] = useState(false);
  const [metric, setMetric] = useState<"mood" | "energy" | "stress">("energy");
  const [chartRange, setChartRange] = useState<ChartRange>(7);
  const [remoteWeekly, setRemoteWeekly] =
    useState<WeeklySummaryResponse | null>(null);
  const [replayId, setReplayId] = useState<string | null>(null);
  const [replayError, setReplayError] = useState<string | null>(null);
  const [tab, setTab] = useState<"story" | "week" | "tools">("story");

  useEffect(() => {
    if (!hydrated) void hydrate();
    // Enough history for 90-day / weekly aggregation views
    void listRecentCheckIns(200).then((rows) => {
      setCheckIns(rows);
      setCheckInsReady(true);
    });
  }, [hydrate, hydrated]);

  const bootLoading =
    (!hydrated && experimentsLoading) || (!checkInsReady && !hydrated);

  // Weekly AI summary loads quietly in the background (no manual refresh button)
  useEffect(() => {
    void (async () => {
      const result = await fetchWeeklySummary();
      if (result.ok) {
        setRemoteWeekly(result.data);
        if (result.revalidating) {
          window.setTimeout(() => {
            void fetchWeeklySummary({ force: true }).then((fresh) => {
              if (fresh.ok) setRemoteWeekly(fresh.data);
            });
          }, 1200);
        }
      }
    })();
  }, []);

  const toolbox = useMemo(
    () => buildToolbox(outcomes, assignments, experiments),
    [outcomes, assignments, experiments]
  );

  const insights = useMemo(
    () => deriveSimpleInsights(outcomes, toolbox),
    [outcomes, toolbox]
  );

  const days = useMemo(
    () => buildChartSeries(checkIns, chartRange),
    [checkIns, chartRange]
  );

  const categoryCounts = useMemo(() => {
    if (remoteWeekly?.stats.topCategories?.length) {
      return remoteWeekly.stats.topCategories;
    }
    const map = new Map<string, number>();
    for (const item of toolbox) {
      map.set(item.category, (map.get(item.category) ?? 0) + item.timesCompleted);
    }
    return [...map.entries()].map(([category, count]) => ({ category, count }));
  }, [remoteWeekly, toolbox]);

  const avgEnergy =
    remoteWeekly?.stats.avgEnergy ??
    (() => {
      const vals = checkIns
        .map((c) => c.energy)
        .filter((v): v is number => v != null);
      if (!vals.length) return null;
      return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
    })();

  const avgStress =
    remoteWeekly?.stats.avgStress ??
    (() => {
      const vals = checkIns
        .map((c) => c.stress)
        .filter((v): v is number => v != null);
      if (!vals.length) return null;
      return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
    })();

  const main = useMemo(
    () =>
      buildMainInsight({
        checkIns,
        outcomes,
        toolbox,
        avgEnergy,
        avgStress,
      }),
    [checkIns, outcomes, toolbox, avgEnergy, avgStress]
  );

  const replayExperiment = async (experimentId: string) => {
    setReplayError(null);
    setReplayId(experimentId);
    const assignment = await assignFromCatalog(experimentId);
    setReplayId(null);
    if (!assignment) {
      setReplayError(t("insights.replayError"));
      return;
    }
    void navigate({
      to: "/experiment/$assignmentId",
      params: { assignmentId: assignment.id },
    });
  };

  if (bootLoading) {
    return <Screen loading skeleton loadingLabel={t("loadingShort")} />;
  }

  const topTool = toolbox[0] ?? null;

  return (
    <Screen>
      <header className="shrink-0 space-y-1">
        <Text variant="title" as="h1">
          {t("insights.title")}
        </Text>
        <Text variant="muted" className="line-clamp-1 text-sm leading-snug">
          {t("insights.leadValue")}
        </Text>
      </header>

      <div className="flex shrink-0 gap-1.5">
        {(
          [
            ["story", t("insights.tabStory")],
            ["week", t("insights.tabOverTime")],
            ["tools", t("insights.tabTools")],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={
              tab === id
                ? "flex-1 rounded-full bg-primary px-2 py-2 text-xs font-medium text-primary-foreground"
                : "flex-1 rounded-full bg-surface-muted px-2 py-2 text-xs font-medium text-foreground-muted"
            }
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-2.5 pb-1">
        {tab === "story" ? (
          <>
            <Card className="space-y-2 border-primary/20 bg-primary-soft/30 p-4">
              <Text variant="label">{t("insights.mainLabel")}</Text>
              <Text variant="heading" as="h2" className="text-lg leading-snug">
                {main.headline}
              </Text>
              <Text variant="body" className="text-sm leading-snug text-foreground-muted">
                {main.body}
              </Text>
            </Card>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {main.signals.slice(0, 4).map((s) => (
                <Card key={s.key} padding="sm" className="space-y-0.5 p-3">
                  <Text variant="caption">{s.label}</Text>
                  <Text variant="heading" as="p" className="text-lg tabular-nums">
                    {s.value}
                  </Text>
                </Card>
              ))}
            </div>

            <Card className="space-y-2 p-4">
              <Text variant="label">{t("insights.nextStep")}</Text>
              <Text variant="body" className="text-sm leading-snug">
                {remoteWeekly?.suggestion ?? main.nextStep}
              </Text>
              {insights
                .filter((i) => i.type === "pattern")
                .slice(0, 2)
                .map((i) => (
                  <Text
                    key={i.id}
                    variant="subtle"
                    className="text-sm leading-snug"
                  >
                    · {i.text}
                  </Text>
                ))}
            </Card>

            <Button
              fullWidth
              className="min-h-11"
              onClick={() => void navigate({ to: "/app/today" })}
            >
              {main.stage === "empty" || main.stage === "early"
                ? t("insights.ctaStart")
                : t("insights.ctaContinue")}
            </Button>
          </>
        ) : null}

        {tab === "week" ? (
          <Card className="space-y-3 p-4">
            <RangeToggle
              value={chartRange}
              onChange={setChartRange}
              options={[
                { value: 7, label: t("insights.range7") },
                { value: 30, label: t("insights.range30") },
                { value: 90, label: t("insights.range90") },
              ]}
            />
            <Text variant="caption" className="leading-snug text-foreground-subtle">
              {chartRange === 90
                ? t("insights.range90Hint")
                : t("insights.emptyBars")}
            </Text>
            <MetricToggle
              value={metric}
              onChange={setMetric}
              labels={{
                mood: t("insights.metricMood"),
                energy: t("insights.metricEnergy"),
                stress: t("insights.metricStress"),
              }}
            />
            <WeekChart days={days} metric={metric} />
            {categoryCounts.length > 0 ? (
              <CategoryHeat categories={categoryCounts} />
            ) : null}
          </Card>
        ) : null}

        {tab === "tools" ? (
          <>
            {replayError ? (
              <Text variant="caption" className="text-danger">
                {replayError}
              </Text>
            ) : null}
            {topTool ? (
              <Card className="space-y-3 p-4">
                {isCategory(topTool.category) ? (
                  <Badge category={topTool.category} />
                ) : null}
                <Text variant="label">{t("insights.yourBest")}</Text>
                <Text variant="heading" as="h3">
                  {topTool.title}
                </Text>
                <Text variant="caption">
                  {t("insights.doneTimes", { count: topTool.timesCompleted })}
                  {topTool.avgRepeatIntent != null &&
                  topTool.avgRepeatIntent >= 1.5
                    ? t("insights.wantedAgain")
                    : ""}
                </Text>
                {topTool.note ? (
                  <Text variant="subtle" className="line-clamp-3 text-sm">
                    “{topTool.note}”
                  </Text>
                ) : (
                  <Text variant="muted" className="text-sm leading-snug">
                    {t("insights.toolsHint")}
                  </Text>
                )}
                <Button
                  fullWidth
                  variant="secondary"
                  loading={replayId === topTool.experimentId}
                  onClick={() => void replayExperiment(topTool.experimentId)}
                >
                  {t("insights.doAgain")}
                </Button>
              </Card>
            ) : (
              <Card className="space-y-3 p-4">
                <Text variant="heading" as="h3">
                  {t("insights.toolboxEmpty")}
                </Text>
                <Text variant="muted" className="text-sm leading-snug">
                  {t("insights.toolsEmptyBody")}
                </Text>
                <Button
                  fullWidth
                  onClick={() => void navigate({ to: "/app/explore" })}
                >
                  {t("insights.ctaExplore")}
                </Button>
              </Card>
            )}
            {toolbox.length > 1 ? (
              <div className="space-y-1.5">
                {toolbox.slice(1, 4).map((item) => (
                  <button
                    key={item.experimentId}
                    type="button"
                    className="flex w-full items-center justify-between rounded-xl border border-border bg-surface-raised px-3 py-2.5 text-left"
                    onClick={() => void replayExperiment(item.experimentId)}
                  >
                    <span className="line-clamp-1 text-sm font-medium">
                      {item.title}
                    </span>
                    <span className="tabular-nums text-xs text-foreground-subtle">
                      ×{item.timesCompleted}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </Screen>
  );
}
